import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ExportFormat,
  EXPORT_FILE_NAME_SUFFIX,
  FORMAT_CSV,
  FORMAT_PDF,
  FORMAT_XLSX,
  FULL_MAP,
  MODULE_CUSTOMER,
  MODULE_ORDER,
  MODULE_SAQ,
  MODULE_SSI_UPLINE,
  STATUS_DONE,
  STATUS_ERROR,
  STATUS_IN_PROGRESS,
  STATUS_PENDING,
} from "../../../../common/constants/export-constants";
import { IconCsv } from "../../../../common/icons/IconCsv/IconCsv";
import { IconDelete } from "../../../../common/icons/IconDelete/IconDelete";
import { IconPdf } from "../../../../common/icons/IconPdf/IconPdf";
import { IconXls } from "../../../../common/icons/IconXls/IconXls";
import LeafDateTime from "../../../../common/leaf/LeafDateTime/LeafDateTime";
import LeafFilterButton from "../../../../common/leaf/LeafFilter/LeafFilterButton/LeafFilterButton";
import { unixEpochMilliSecToISO } from "../../../../common/util/formatDate";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import defaultContent from "../../../../content/serverSideExport";
import { getCountryNameFromCode } from "../../../../common/util/formatCountryName";
import { getCountryCodeFromAff } from "../../../../common/util/getCountryFlag";
import "./ServerSideExportDownload.scss";
import { ExportDetails } from "../../../../services/ExportV2/exportV2API.types";
import {
  useDeleteExportV2Mutation,
  useLazyGetExportDownloadV2Query,
  useLazyGetExportNotificationV2Query,
  util as ExportAPIUtil,
} from "../../../../services/ExportV2/exportV2API";
import { fileNameBuilder, losFileNameBuilder } from "../ServerSideExportUtil/ServerSideExportFileName";
import { useAppDispatch } from "../../../../store";
import { dispatchExportCountEvent } from "../ServerSideExportUtil/ServerSideExportEvents";
import DownloadList from "./Loader/DownloadList/DownloadList";
import { deleteExportId } from "../../../../reducers/export";
import { isExportDisabledBySessionSsoOrigin } from "../../../../common/util/exportUtil";
import ServerSideExportStatus from "../ServerSideExportStatus/ServerSideExportStatus";
import { IconRotatingCircle } from "../../../../common/icons/IconRotatingCircle/IconRotatingCircle";

type Props = {
  title: string;
  subTitle: string;
  exportTypeContent: {
    fullMap?: string;
    fullMapAllKPIs?: string;
    currentView?: string;
  };
  startedByLabels?: Record<string, string>;
  exports?: ExportDetails[];
  exportAnotherButton?: {
    exportText: string;
    exportAnotherText: string;
    active?: boolean;
    onClick: (showExports: boolean) => void;
  };
  showExportAnotherButton?: boolean;
  moduleId: string;
  moduleName?: string;
  show: boolean;
  downloadButtonOnClick: (exportDetails: ExportDetails, fileFormat: string, moduleId: string) => void;
  deleteButtonOnClick: (exportDetails: ExportDetails, moduleId: string) => void;
  isFetching: boolean;
  supportedFormats?: ExportFormat[];
};

const ServerSideExportDownload = ({
  title,
  subTitle,
  exportTypeContent,
  startedByLabels,
  exports = [],
  exportAnotherButton,
  showExportAnotherButton,
  downloadButtonOnClick,
  deleteButtonOnClick,
  moduleId,
  moduleName,
  show,
  isFetching,
  supportedFormats = [FORMAT_XLSX, FORMAT_CSV, FORMAT_PDF],
}: Props) => {
  const { t } = useTranslation(["serverSideExport"]);
  const dispatch = useAppDispatch();
  const { los, cml } = isExportDisabledBySessionSsoOrigin();
  const isExportDisabled = los && cml;

  const locale = storedLocale();

  const showExports = exports.length > 0;

  const {
    exportText = "",
    exportAnotherText = "",
    active: activeExportAnotherButton = true,
  } = exportAnotherButton || {};

  const options = {
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    year: "numeric",
    month: "short",
    hour12: true,
  } as Intl.DateTimeFormatOptions;

  const [getExportDownload] = useLazyGetExportDownloadV2Query();
  const [deleteExport] = useDeleteExportV2Mutation();
  const [exportNotificationQuery] = useLazyGetExportNotificationV2Query();

  const updateExportList = (exportList: ExportDetails[]) => {
    dispatch(
      ExportAPIUtil.upsertQueryData(
        "getExportListV2",
        { moduleId },
        {
          Exports: exportList,
        },
      ),
    );
  };

  useMemo(() => {
    if (exports.length === 0) {
      dispatchExportCountEvent(0, 0);
    }
  }, [exports]);

  const setDownloadIconLoading = (isFetching: boolean, targetExportDetails: ExportDetails, fileFormat: string) => {
    const isPDF = fileFormat === FORMAT_PDF;
    const isExcel = fileFormat === FORMAT_XLSX;
    const isCSV = fileFormat === FORMAT_CSV;

    const updatedExportList = exports.map((details) => {
      if (details.ExportID === targetExportDetails.ExportID) {
        return {
          ...details,
          PDFLoading: isPDF && isFetching,
          ExcelLoading: isExcel && isFetching,
          CSVLoading: isCSV && isFetching,
        };
      }
      return details;
    });

    updateExportList(updatedExportList);
  };

  const exportDownloadButtonOnClick = async (exportDetails: ExportDetails, fileFormat: string, moduleId: string) => {
    downloadButtonOnClick(exportDetails, fileFormat, moduleId);

    const { ExportID } = exportDetails;
    setDownloadIconLoading(true, exportDetails, fileFormat);

    const reportSuffix = (() => {
      if (moduleId === MODULE_SAQ && exportDetails.StartedBy) return exportDetails.StartedBy;
      if (EXPORT_FILE_NAME_SUFFIX[moduleId]) {
        return String(
          t(
            `exportFileNameSuffix.${moduleId}`,
            defaultContent.exportFileNameSuffix[moduleId] || EXPORT_FILE_NAME_SUFFIX[moduleId],
          ),
        );
      }
      return undefined;
    })();

    const downloadFileName =
      moduleId === MODULE_CUSTOMER || moduleId === MODULE_ORDER || moduleId === MODULE_SSI_UPLINE
        ? fileNameBuilder(moduleName || moduleId)
        : losFileNameBuilder(exportDetails.Period, reportSuffix);

    const { data } = await getExportDownload({
      ExportID,
      FileFormat: fileFormat,
      DownloadFileName: downloadFileName,
    });

    const link = document.createElement("a");
    link.href = data?.DownloadLink || "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setDownloadIconLoading(false, exportDetails, fileFormat);
  };

  const exportDeleteButtonOnClick = (targetExportDetails: ExportDetails, moduleId: string) => {
    const { ExportID } = targetExportDetails;

    const updatedExportList = exports.filter((details) => details.ExportID !== ExportID);
    updateExportList(updatedExportList);

    deleteExport({ exportId: ExportID, moduleId });

    dispatch(deleteExportId({ moduleId, exportId: ExportID }));
    if (!isExportDisabled) {
      exportNotificationQuery({ moduleId }, false);
    }

    deleteButtonOnClick(targetExportDetails, moduleId);
  };

  const showIcon = (status: string) => {
    return [STATUS_IN_PROGRESS, STATUS_ERROR, STATUS_PENDING].includes(status) === false;
  };

  const getExportTypeTitle = (exp: ExportDetails) => {
    switch (exp.ExportType) {
      case FULL_MAP:
        if (exp.FilteredByKPIS === true) {
          return exportTypeContent.fullMap || "";
        } else {
          return exportTypeContent.fullMapAllKPIs || "";
        }
      default:
        if (startedByLabels && exp.StartedBy && startedByLabels[exp.StartedBy]) {
          return startedByLabels[exp.StartedBy];
        }
        return exportTypeContent.currentView || "";
    }
  };

  const getDoneMessage = (expiresInSeconds: number) => {
    const hours = expiresInSeconds / 3600;
    const moreThan1Hrs = hours > 1;
    const left1Hr = hours === 1;

    const lessThanMsg =
      moreThan1Hrs || left1Hr
        ? ""
        : `${t(
            "availableExportModal.exportFile.lessThan",
            defaultContent["availableExportModal"]["exportFile"]["lessThan"],
          )}`;
    const remainingHrs = moreThan1Hrs ? Math.floor(hours) : 1;
    const hourMsg = moreThan1Hrs
      ? `${t(
          "availableExportModal.exportFile.doneHoursMessage",
          defaultContent["availableExportModal"]["exportFile"]["doneHoursMessage"],
        )}`
      : `${t(
          "availableExportModal.exportFile.doneHourMessage",
          defaultContent["availableExportModal"]["exportFile"]["doneHourMessage"],
        )}`;

    return ` - ${t(
      "availableExportModal.exportFile.doneExpiresInMessage",
      defaultContent["availableExportModal"]["exportFile"]["doneExpiresInMessage"],
    )} ${lessThanMsg} ${remainingHrs} ${hourMsg} `;
  };

  const getAboNumber = (exp: ExportDetails) => {
    if (exp.ReportAbo && String(exp.ReportAbo) !== "0") {
      return exp.ReportAbo;
    }
    return exp.ReqAffAbo?.split("-")[1] || exp.ReqAffAbo;
  };

  const getCountryCode = (exp: ExportDetails) => {
    return exp.CountryCode || getCountryCodeFromAff(exp.ReqAffAbo?.split("-")[0] || "");
  };

  const getExportStatusMessage = (exp: ExportDetails) => {
    switch (exp.ExportStatus) {
      case STATUS_ERROR:
        return `${t(
          "availableExportModal.exportFile.exportError",
          defaultContent["availableExportModal"]["exportFile"]["exportError"],
        )}`;
      case STATUS_IN_PROGRESS:
      case STATUS_PENDING:
        return `${t(
          "availableExportModal.exportFile.inProgressMessage",
          defaultContent["availableExportModal"]["exportFile"]["inProgressMessage"],
        )}`;
      case STATUS_DONE:
        return getDoneMessage(exp.ExpiresInSeconds);
    }
  };

  const getSpinningIcon = (exp: ExportDetails) => {
    const isExporting = exp.ExportStatus === STATUS_IN_PROGRESS || exp.ExportStatus === STATUS_PENDING;
    return isExporting ? <IconRotatingCircle /> : <></>;
  };

  if (show === false) return <></>;

  return (
    <div className="server-side-export-download" data-testid="export-download-content">
      <div className="server-side-export-download__header">
        <div className="server-side-export-download__header__title">{title}</div>
        {showExports === true && <div className="server-side-export-download__header__subtitle">{subTitle}</div>}
      </div>
      <div className="server-side-export-download__content">
        {isFetching === false && showExports === false && (
          <div className="server-side-export-download__no-content">
            {activeExportAnotherButton === true ? (
              <Trans>
                {t(
                  "availableExportModal.noExportsAvailable",
                  defaultContent["availableExportModal"]["noExportsAvailable"],
                ).toString()}
              </Trans>
            ) : (
              <Trans>
                {t(
                  "availableExportModal.noDataAvailable",
                  defaultContent["availableExportModal"]["noDataAvailable"],
                ).toString()}
              </Trans>
            )}
          </div>
        )}
        <DownloadList numberOfLoaders={1} isFetching={isFetching} />
        {exports.map((exp, index) => (
          <div className="server-side-export-download__export-detail" data-testid="export-detail-row" key={index}>
            <div className="server-side-export-download__export-type">{getExportTypeTitle(exp)}</div>
            <div className="server-side-export-download__divider-line"></div>
            <div className="server-side-export-download__export-period">
              {`${t(
                "availableExportModal.exportFile.period",
                defaultContent["availableExportModal"]["exportFile"]["period"],
              )}`}{" "}
              {getLongPeriodFormat(exp.Period, locale)}
            </div>
            <div className="server-side-export-download__affAbo-country">
              <Trans>
                {String(
                  t(
                    "availableExportModal.exportFile.aboDetail",
                    defaultContent["availableExportModal"]["exportFile"]["aboDetail"],
                    { aboNo: getAboNumber(exp), country: getCountryNameFromCode(getCountryCode(exp), locale) },
                  ),
                )}
              </Trans>
            </div>
            {exp.GroupLeader && (
              <div className="server-side-export-download__group-leader" data-testid="export-group-leader-row">
                <Trans>
                  {String(
                    t(
                      "availableExportModal.exportFile.groupLeader",
                      defaultContent["availableExportModal"]["exportFile"]["groupLeader"],
                      { aboNo: `${exp.GroupLeader.aff}-${exp.GroupLeader.abo}` },
                    ),
                  )}
                </Trans>
              </div>
            )}
            <div className="server-side-export-download__start-time">
              <Trans>
                {t(
                  "availableExportModal.exportFile.startTime",
                  defaultContent["availableExportModal"]["exportFile"]["startTime"],
                ).toString()}
              </Trans>{" "}
              <LeafDateTime date={unixEpochMilliSecToISO(exp.StartTime)} options={options} ignoreTimeZone={false} />
            </div>
            <div className="server-side-export-download__icons">
              <div
                className={`server-side-export-download__download-icons server-side-export-download__download-icons${
                  showIcon(exp.ExportStatus) ? "--show" : ""
                }`}
              >
                {supportedFormats.includes(FORMAT_XLSX) && (
                  <div
                    tabIndex={0}
                    role="button"
                    onClick={() => exportDownloadButtonOnClick(exp, FORMAT_XLSX, moduleId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        exportDownloadButtonOnClick(exp, FORMAT_XLSX, moduleId);
                      }
                    }}
                    aria-label={`${t(
                      "availableExportModal.excelLabel",
                      defaultContent["availableExportModal"]["excelLabel"],
                    )}`}
                    className="server-side-export-download__icon"
                    data-testid="export-download-xlsx"
                  >
                    <IconXls loading={exp.ExcelLoading} />
                  </div>
                )}
                {supportedFormats.includes(FORMAT_CSV) && (
                  <div
                    tabIndex={0}
                    role="button"
                    onClick={() => exportDownloadButtonOnClick(exp, FORMAT_CSV, moduleId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        exportDownloadButtonOnClick(exp, FORMAT_CSV, moduleId);
                      }
                    }}
                    aria-label={`${t(
                      "availableExportModal.csvLabel",
                      defaultContent["availableExportModal"]["csvLabel"],
                    )}`}
                    className="server-side-export-download__icon"
                    data-testid="export-download-csv"
                  >
                    <IconCsv loading={exp.CSVLoading} />
                  </div>
                )}
                {supportedFormats.includes(FORMAT_PDF) && (
                  <div
                    tabIndex={0}
                    role="button"
                    onClick={() => exportDownloadButtonOnClick(exp, FORMAT_PDF, moduleId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        exportDownloadButtonOnClick(exp, FORMAT_PDF, moduleId);
                      }
                    }}
                    aria-label={`${t(
                      "availableExportModal.pdfLabel",
                      defaultContent["availableExportModal"]["pdfLabel"],
                    )}`}
                    className="server-side-export-download__icon"
                    data-testid="export-download-pdf"
                  >
                    <IconPdf loading={exp.PDFLoading} />
                  </div>
                )}
                <div className="server-side-export-download__icon-divider"></div>
              </div>
              <div
                tabIndex={0}
                role="button"
                aria-label={`${t(
                  "availableExportModal.deleteLabel",
                  defaultContent["availableExportModal"]["deleteLabel"],
                )}`}
                className="server-side-export-download__icon"
                data-testid="export-download-delete"
                onClick={() => exportDeleteButtonOnClick(exp, moduleId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    exportDeleteButtonOnClick(exp, moduleId);
                  }
                }}
              >
                <IconDelete />
              </div>
            </div>
            <div className="server-side-export-download__export-status">
              <div
                className={`server-side-export-download__ready server-side-export-download__ready--${exp.ExportStatus}`}
              >
                {`${t(
                  "availableExportModal.exportFile.doneBoldMessage",
                  defaultContent["availableExportModal"]["exportFile"]["doneBoldMessage"],
                )}`}
              </div>
              <div
                className={`server-side-export-download__status-message server-side-export-download__status-message--${exp.ExportStatus}`}
              >
                <span>{getExportStatusMessage(exp)}</span>
                {getSpinningIcon(exp)}
              </div>
            </div>
            <div className={`server-side-export-download__export-progress-bar`}>
              <ServerSideExportStatus
                progressPercent={exp.ProgressPercent}
                isExporting={exp.ExportStatus === STATUS_IN_PROGRESS || exp.ExportStatus === STATUS_PENDING}
                isExported={exp.ExportStatus === STATUS_DONE}
                isFailed={exp.ExportStatus === STATUS_ERROR}
              />
            </div>
          </div>
        ))}
      </div>
      {showExportAnotherButton === true && (
        <div className="server-side-export-download__footer">
          <LeafFilterButton
            disabled={!activeExportAnotherButton}
            text={showExports ? exportAnotherText : exportText}
            onButtonClick={() => {
              if (activeExportAnotherButton) {
                exportAnotherButton?.onClick(showExports);
              }
            }}
            theme="dark"
            dataTestId="export-another"
          />
        </div>
      )}
    </div>
  );
};

export default ServerSideExportDownload;
